import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { EditorContent, Editor as TiptapEditor } from '@tiptap/react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Switch } from '@flintmint/web-shared/components/switch';
import { Popover } from '@flintmint/web-shared/components/popover';
import { Divider } from '@flintmint/web-shared/components/divider';
import { Collapse } from '@flintmint/web-shared/components/collapse';
import { InlineInput } from '@flintmint/web-shared/components/inline-input';

import { useAutosave } from '@/hooks/use-autosave';
import { useItemActions } from '@/hooks/use-item-actions';
import { useInstructionEditor } from '@/hooks/use-instruction-editor';
import { useInstructionUpdateById } from '@/hooks/rxdb-models';

import { copyToClipboard } from '@/utils/helper-utils';
import { getEditorHTML, isEditorAlive } from '@/utils/editor-utils';
import { conditionalSpread } from '@flintmint/web-shared/utils/helper-utils';

import type { DocumentWithIdNameContent, DocumentWithType } from '@/constants/types';

import styles from './instruction.module.scss';

export const Instruction = (props: InstructionProps) => {
  const {
    enabled,
    focused,
    readOnly,
    expanded,
    onDelete,
    instruction,
    toggleExpanded,
    onEnabledChange,
    onInstructionUpdate,
    removeExternalFocus,
    showDeleteConfirmation = true,
    editorTestId = 'instruction-editor',
    expandArrowTestId = 'instruction-expand',
  } = props;

  const [name, setName] = useState(instruction.name);
  const [content, setContent] = useState(instruction.content);

  const ref = useRef<HTMLDivElement>(null);
  const isDirtyRef = useRef(false);
  const isDeletingRef = useRef(false);

  const { mutate: updateInstruction } = useInstructionUpdateById();
  const { configureDeleteOption } = useItemActions();

  const editable = !readOnly;
  // if content is editable, it should always be expanded
  const isExpanded = editable || expanded;

  const scrollToCursor = (editor: TiptapEditor) => {
    const selection = editor.view.state.selection;
    const domAtPos = editor.view.domAtPos(selection.head);
    const domNode = domAtPos.node as HTMLElement;

    domNode?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  };

  const editor = useInstructionEditor({
    content,
    onUpdate: ({ editor }) => {
      const html = getEditorHTML(editor as TiptapEditor);
      isDirtyRef.current = true;
      setContent(html);
      scrollToCursor(editor as TiptapEditor);
    },
    onFocus: () => {
      if (focused) {
        requestAnimationFrame(() => {
          scrollIntoView(ref?.current!);
          removeExternalFocus();
        });
      }
    },
  });

  useEffect(() => {
    // Always reflect the latest external instruction name in the local state.
    // This ensures that when instruction.name changes (e.g. from another source),
    // the input value is updated accordingly.
    setName(instruction.name);
  }, [instruction.name]);

  useEffect(() => {
    const incoming = instruction.content;

    // While the editor is focused, treat local edits as the source of truth to avoid cursor jumps.
    // Only pull in external content updates when the editor is not focused.
    if (isEditorAlive(editor) && !editor.isFocused && incoming !== getEditorHTML(editor) && !isDirtyRef.current) {
      setContent(incoming);
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [instruction.content, editor]);

  const { flush } = useAutosave({
    initialData: {
      name: instruction.name,
      content: instruction.content,
    },
    data: {
      name,
      content,
    },
    onSave: (payload) => {
      return updateInstruction(instruction.id, payload, {
        onSuccess: () => {
          isDirtyRef.current = false;
          onInstructionUpdate?.();
        },
      });
    },
  });

  useEffect(() => {
    return () => {
      if (!editable || isDeletingRef.current) return;

      flush().catch((error) => {
        console.error('Failed to flush instruction autosave', error);
      });
    };
  }, [editable, flush]);

  const markGone = () => {
    isDeletingRef.current = true;
  };

  const notifyParentRemoved = async () => {
    markGone();
    try {
      const didRemove = await onDelete(instruction.id);
      if (didRemove === false) {
        isDeletingRef.current = false;
      }
    } catch (error) {
      isDeletingRef.current = false;
      throw error;
    }
  };

  const instructionSelection: DocumentWithType[] = [{ id: instruction.id, name, type: 'instructions' }];

  const menu = [
    {
      name: 'Copy',
      icon: 'copy-01',
      onClick: () => {
        copyToClipboard({ content, textContent: editor?.getText() });
      },
    },
    {
      type: 'divider',
    },
    // TODO Temporary: hide Move to archive until instructions library exists. Delete stays.
    // Permanent delete drops soft refs via cleanup; attached counts drop via useResolvedInstructionIds.
    ...conditionalSpread(
      [configureDeleteOption(instructionSelection, { onConfirm: markGone, onDone: notifyParentRemoved })],
      showDeleteConfirmation
    ),
    ...conditionalSpread(
      [
        {
          name: 'Delete',
          danger: true,
          icon: 'delete-01',
          onClick: notifyParentRemoved,
        },
      ],
      !showDeleteConfirmation
    ),
  ];

  useEffect(() => {
    focused && editor?.commands.focus('start', { scrollIntoView: true });
  }, [focused]);

  return (
    <div
      ref={ref}
      className={clsx(styles.instruction, {
        [styles.enabled]: enabled,
        [styles.editable]: !readOnly,
      })}
    >
      <ul className={styles.header}>
        <li>
          <InlineInput
            value={name}
            readOnly={readOnly}
            placeholder="Instruction #"
            data-testid="instruction-name-input"
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <Popover menu={menu} placement="bottom" maxWidth={164}>
            <Icon type="more-horizontal" data-testid="menu" className={styles.menu} />
          </Popover>
        </li>
        <li
          className={clsx(styles.actions, {
            [styles.expanded]: expanded,
          })}
        >
          <Switch checked={enabled} onChange={(newEnabledVal) => onEnabledChange(instruction.id, newEnabledVal)} />
          {readOnly && (
            <Icon type="arrow-up-01-sharp" data-testid={expandArrowTestId} onClick={() => toggleExpanded(instruction.id)} />
          )}
        </li>
      </ul>
      <Collapse open={isExpanded}>
        <Divider className={styles.divider} orientation="horizontal" />
        <EditorContent
          editor={editor}
          readOnly={readOnly}
          data-testid={!readOnly ? editorTestId : undefined}
          className={styles.instructionEditor}
        />
      </Collapse>
    </div>
  );
};

export interface InstructionProps {
  enabled: boolean;
  focused: boolean;
  expanded: boolean;
  readOnly?: boolean;
  editorTestId?: string;
  expandArrowTestId?: string;
  removeExternalFocus: () => void;
  showDeleteConfirmation?: boolean;
  onInstructionUpdate?: () => void;
  instruction: DocumentWithIdNameContent;
  toggleExpanded: (instructionId: string) => void;
  onEnabledChange: (instructionId: string, enabled: boolean) => void;
  onDelete: (instructionId: string) => void | boolean | Promise<void | boolean>;
}
